import { Fragment, type MutableRefObject } from "react";
import { Transition, Dialog, DialogPanel, TransitionChild, DialogTitle } from "@headlessui/react";
import cn from "classnames";
import XIcon from "@icons/x-close.svg?react";

interface HDDialogProps {
    isOpen: boolean;
    handleClose: () => void;
    header?: React.ReactNode;
    subHeader?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    id?: string;
    className?: string;
    /**
     * Replaces the default panel width (`w-[80%] sm:max-w-xl`) instead of adding to it. classnames does not
     * resolve Tailwind conflicts, so a width in `className` can lose to the default.
     */
    widthClassName?: string;
    closeOnClickOutside?: boolean;
    /** When true, no border is shown between content and footer. */
    hideContentFooterBorder?: boolean;
    /** When true, uses minimal padding and gap so the modal fits tightly around content. */
    compact?: boolean;
    /** When set, Headless UI focuses this element when the dialog opens instead of the first tabbable control. */
    initialFocus?: MutableRefObject<HTMLElement | null>;
    /**
     * Accessible name for the dialog, for a caller that does not pass `header`.
     *
     * `DialogTitle` is only rendered inside `header` below, and it is what otherwise gives the
     * dialog its name — so a dialog whose heading lives in its own children is announced as an
     * unnamed dialog. Screen readers get nothing to say beyond "dialog".
     */
    ariaLabel?: string;
    /** When true, the header has no close X: for a dialog the user must answer, such as a choice with no safe default. */
    hideCloseButton?: boolean;
}

const HDDialog = ({
    isOpen,
    handleClose,
    header,
    subHeader,
    children,
    footer,
    id,
    className,
    widthClassName = "w-[80%] sm:max-w-xl",
    closeOnClickOutside = true,
    hideContentFooterBorder = false,
    compact = false,
    initialFocus,
    ariaLabel,
    hideCloseButton = false,
}: HDDialogProps): React.JSX.Element => {
    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog
                id={id}
                as="div"
                aria-label={ariaLabel}
                className="relative z-50"
                onClose={closeOnClickOutside ? handleClose : () => {}}
                static
                initialFocus={initialFocus}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center text-center items-center">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                className={cn(
                                    "relative transform overflow-x-hidden overflow-y-auto rounded-lg bg-white text-left shadow-xl transition-all",
                                    widthClassName,
                                    className,
                                )}
                            >
                                <div
                                    className={cn(
                                        "flex flex-col",
                                        compact ? "p-0 gap-0" : "pt-4 md:pt-5 pb-4 md:pb-6 gap-4 md:gap-6",
                                    )}
                                >
                                    {header && (
                                        <div className="border-gray-200 pb-3 md:pb-4 px-4 md:px-6 border-b">
                                            <DialogTitle
                                                as="h3"
                                                className="text-lg font-semibold leading-6 text-gray-900 text-left flex flex-row items-center justify-between relative"
                                            >
                                                {header}
                                                {!hideCloseButton && (
                                                    <button
                                                        type="button"
                                                        aria-label="Close dialog"
                                                        className="focused-inset rounded-md"
                                                        onClick={handleClose}
                                                    >
                                                        <XIcon className="h-6 w-6 text-gray-400 hover:text-gray-900 cursor-pointer" />
                                                    </button>
                                                )}
                                            </DialogTitle>
                                            {subHeader && (
                                                <div className="mt-2 md:-mb-1 text-sm text-gray-700">{subHeader}</div>
                                            )}
                                        </div>
                                    )}

                                    {children && (
                                        <div
                                            className={cn(
                                                "flex flex-col",
                                                compact ? "px-6" : "px-4 md:px-6 pb-4 md:pb-6 space-y-4",
                                                Boolean(footer) && !hideContentFooterBorder && "border-b",
                                            )}
                                        >
                                            {children}
                                        </div>
                                    )}

                                    {footer && (
                                        <div
                                            className={cn(
                                                "flex justify-end items-center space-x-4",
                                                compact ? "px-6 py-3" : "px-4 md:px-6",
                                            )}
                                        >
                                            {footer}
                                        </div>
                                    )}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default HDDialog;
